import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SequenceCalculator = () => {
  const [inputData, setInputData] = useState({
    sequenceType: 'arithmetic',
    firstTerm: '',
    commonDifference: '',
    commonRatio: '',
    terms: '',
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateSequence = () => {
    const { sequenceType, firstTerm, commonDifference, commonRatio, terms } = inputData;

    // Validate inputs
    if (!firstTerm || !terms) {
      setError('Please enter the first term and number of terms');
      return;
    }

    const firstTermNum = parseFloat(firstTerm);
    const termsNum = parseInt(terms);

    if (isNaN(firstTermNum) || isNaN(termsNum) || termsNum <= 0) {
      setError('Please enter valid numbers for first term and terms (terms must be positive integer)');
      return;
    }

    let sequence = [];
    let formula = '';
    let additionalInfo = '';

    switch (sequenceType) {
      case 'arithmetic':
        if (!commonDifference) {
          setError('Please enter the common difference for arithmetic sequence');
          return;
        }
        const difference = parseFloat(commonDifference);
        sequence = Array.from({ length: termsNum }, (_, i) => 
          firstTermNum + difference * i
        );
        formula = `aₙ = a₁ + (n - 1)d\nWhere:\na₁ = ${firstTermNum}, d = ${difference}`;
        additionalInfo = `nth term: ${firstTermNum + difference * (termsNum - 1)}`;
        break;

      case 'geometric':
        if (!commonRatio) {
          setError('Please enter the common ratio for geometric sequence');
          return;
        }
        const ratio = parseFloat(commonRatio);
        sequence = Array.from({ length: termsNum }, (_, i) => 
          firstTermNum * Math.pow(ratio, i)
        );
        formula = `aₙ = a₁ × r^(n-1)\nWhere:\na₁ = ${firstTermNum}, r = ${ratio}`;
        additionalInfo = `nth term: ${firstTermNum * Math.pow(ratio, termsNum - 1)}`;
        break;

      case 'fibonacci':
        if (termsNum < 2) {
          sequence = [0];
        } else if (termsNum === 2) {
          sequence = [0, 1];
        } else {
          sequence = [0, 1];
          for (let i = 2; i < termsNum; i++) {
            sequence.push(sequence[i - 1] + sequence[i - 2]);
          }
        }
        formula = 'Fₙ = Fₙ₋₁ + Fₙ₋₂\nWith F₀ = 0, F₁ = 1';
        additionalInfo = `Golden ratio approximation: ${sequence.length > 2 ? (sequence[sequence.length - 1] / sequence[sequence.length - 2]).toFixed(4) : 'N/A'}`;
        break;

      default:
        setError('Invalid sequence type selected');
        return;
    }

    // Create visualization data
    const chartData = {
      labels: Array.from({ length: termsNum }, (_, i) => `Term ${i + 1}`),
      datasets: [{
        label: 'Sequence Values',
        data: sequence,
        fill: false,
        tension: 0.1,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      }]
    };

    setResult({
      sequenceType,
      sequence,
      formula,
      additionalInfo,
      chartData
    });
  };

  const exampleData = {
    sequenceType: 'arithmetic',
    firstTerm: 2,
    commonDifference: 3,
    terms: 5,
    sequence: [2, 5, 8, 11, 14]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-cyan-100 to-blue-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Sequence Calculator | Arithmetic, Geometric, Fibonacci</title>
          <meta name="description" content="Calculate arithmetic, geometric, and Fibonacci sequences. This calculator provides sequence terms, formulas, and visualizations for educational purposes." />
          <meta name="keywords" content="sequence calculator, arithmetic sequence, geometric sequence, fibonacci sequence, math calculator, sequence generator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-cyan-600 mr-4">🔢</div>
            <h1 className="text-3xl font-bold text-gray-800">Sequence Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate arithmetic, geometric, and Fibonacci sequences. This calculator provides sequence terms, formulas, and visualizations to help understand different sequence patterns.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sequence Type:</label>
              <select
                name="sequenceType"
                value={inputData.sequenceType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                <option value="arithmetic">Arithmetic Sequence</option>
                <option value="geometric">Geometric Sequence</option>
                <option value="fibonacci">Fibonacci Sequence</option>
              </select>
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">First Term (a₁):</label>
              <input
                type="number"
                name="firstTerm"
                value={inputData.firstTerm}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="Enter first term"
              />
            </div>

            {inputData.sequenceType === 'arithmetic' && (
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Common Difference (d):</label>
                <input
                  type="number"
                  name="commonDifference"
                  value={inputData.commonDifference}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Enter common difference"
                />
              </div>
            )}

            {inputData.sequenceType === 'geometric' && (
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Common Ratio (r):</label>
                <input
                  type="number"
                  name="commonRatio"
                  value={inputData.commonRatio}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Enter common ratio"
                />
              </div>
            )}

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Terms:</label>
              <input
                type="number"
                name="terms"
                value={inputData.terms}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="Enter number of terms"
                min="1"
              />
            </div>
          </div>

          <button
            onClick={calculateSequence}
            className="w-full py-3 px-4 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Sequence
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Sequence Information</h3>
                  <p className="text-lg">Sequence Type: {result.sequenceType.charAt(0).toUpperCase() + result.sequenceType.slice(1)}</p>
                  <p className="text-lg mt-2">Sequence:</p>
                  <div className="mt-2 whitespace-pre-wrap">{result.sequence.join(', ')}</div>
                  <p className="text-lg mt-4">Formula:</p>
                  <pre className="whitespace-pre-wrap mt-2 font-mono">{result.formula}</pre>
                  <p className="text-lg mt-4">Additional Info:</p>
                  <p className="mt-2">{result.additionalInfo}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Sequence Visualization</h3>
                  <div className="h-64 bg-white rounded-lg flex items-center justify-center">
                    <Line
                      data={result.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Term Value'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Term Position'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Sequence Visualization'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-cyan-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for an arithmetic sequence:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Sequence Type: Arithmetic</li>
                <li>First Term: {exampleData.firstTerm}</li>
                <li>Common Difference: 3</li>
                <li>Terms: {exampleData.terms}</li>
                <li>Sequence: {exampleData.sequence.join(', ')}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Term 1: 2</li>
                <li>Term 2: 2 + 3 = 5</li>
                <li>Term 3: 5 + 3 = 8</li>
                <li>Term 4: 8 + 3 = 11</li>
                <li>Term 5: 11 + 3 = 14</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-cyan-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Sequence Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Select Sequence Type:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose between arithmetic, geometric, or Fibonacci sequence</li>
                </ul>
              </li>
              <li><strong>Enter Required Parameters:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter the first term for all sequence types</li>
                  <li>Enter common difference for arithmetic sequences</li>
                  <li>Enter common ratio for geometric sequences</li>
                  <li>Enter the number of terms you want to generate</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The sequence terms</li>
                  <li>The general formula for the sequence</li>
                  <li>A visual representation of the sequence</li>
                  <li>Additional information specific to the sequence type</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-cyan-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Arithmetic sequences have constant differences between terms</li>
                <li>Geometric sequences have constant ratios between terms</li>
                <li>Fibonacci sequences demonstrate exponential growth patterns</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Sequences <span className="text-cyan-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Sequences are ordered lists of numbers that follow specific patterns:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Arithmetic Sequence:</strong> Each term is obtained by adding a constant difference to the previous term</li>
              <li><strong>Geometric Sequence:</strong> Each term is obtained by multiplying the previous term by a constant ratio</li>
              <li><strong>Fibonacci Sequence:</strong> Each term is the sum of the two preceding terms</li>
            </ul>
            <p className="mt-4">
              These sequences have numerous applications in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Mathematics:</strong> Number theory and pattern recognition</li>
                <li><strong>Computer Science:</strong> Algorithm design and recursion examples</li>
                <li><strong>Finance:</strong> Compound interest calculations</li>
                <li><strong>Nature:</strong> Modeling growth patterns</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-cyan-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Identify Sequence Type:</strong> Determine which sequence pattern fits your data</li>
              <li><strong>Understand Limitations:</strong> Some sequences grow extremely quickly and can exceed standard number limits</li>
              <li><strong>Use for Pattern Recognition:</strong> Sequences help identify and predict numerical patterns</li>
              <li><strong>Explore Real-World Applications:</strong> Connect sequence patterns to practical scenarios</li>
            </ul>
            <p className="italic mt-4">
              Note: For very large sequences or complex patterns, consider using specialized mathematical software.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SequenceCalculator;